import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { Grid, Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { useParams } from "react-router-dom";
import {
  useEditJobCardMutation,
  useGetJobCardByReferenceQuery,
} from "../api/apiSlice";
import CircularProgress from "@mui/material/CircularProgress";
import {
  setCurrentStep,
  getCurrentStep,
  setsteps,
} from "./Stepper/stepperSlice";
import { useSelector, useDispatch } from "react-redux";
import NavbarStepper from "./Stepper/NavBarStepper";
import theme from "../../Global/theme";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AbortJobButton from "./AbortJobButton";
import { showError } from "../../Global/notifications/notificationsSlice";
import stepSaveMessage from "./stepSaveMessage";
import useRedoPhotos from "./useRedoPhotos";

const AMBER = "#FDB940";

// Photos captured on the grouped After Install page (NEW installs — includes clamp meter)
const AFTER_PHOTO_DEFS = [
  { name: "clamp_meter_photo",   label: "Clamp\nMeter" },
  { name: "install_photo_after", label: "Work Area\nAfter Install" },
  { name: "after_photo_off",     label: "DB Board\nCover OFF" },
  { name: "after_photo_on",      label: "DB Board\nCover ON" },
];

// Photos for Uninstall workflows only (post-install clamp meter not applicable)
const AFTER_PHOTO_DEFS_UN_RE = [
  { name: "install_photo_after", label: "Work Area\nAfter Uninstall" },
  { name: "after_photo_off",     label: "DB Board\nCover OFF" },
  { name: "after_photo_on",      label: "DB Board\nCover ON" },
];

// Outer component: fetches the job card to determine install_state,
// then passes the correct photo definitions to the inner component.
function AfterInstallPage() {
  useStepperStateRestoration();
  const { job_card_reference_id } = useParams();
  const { data: jobCard, isSuccess } = useGetJobCardByReferenceQuery(job_card_reference_id);

  if (!isSuccess) return null;

  const photoDefs =
    jobCard.install_state === "UN"
      ? AFTER_PHOTO_DEFS_UN_RE
      : AFTER_PHOTO_DEFS;

  return <AfterInstallPhotos photoDefs={photoDefs} isUninstall={jobCard.install_state === "UN"} />;
}

// Inner component: renders the grouped photo capture UI.
// Receives photoDefs as a prop so useState initializer has the correct length.
function AfterInstallPhotos({ photoDefs, isUninstall }) {
  const {
    isConnected,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails,
  } = useConnectionMonitor();

  const { job_card_reference_id, serial_number, imsi_number } = useParams();
  const step = useSelector(getCurrentStep);
  const navigate = useNavigate();

  const [editJobCard, { isSuccess, isLoading }] = useEditJobCardMutation();
  const dispatch = useDispatch();

  const [photos, setPhotos] = useState(() => photoDefs.map(() => null));
  const { slotNote } = useRedoPhotos(job_card_reference_id, photoDefs, setPhotos);
  const [activeRetakeIndex, setActiveRetakeIndex] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Restore photos from localStorage on mount
  useEffect(() => {
    photoDefs.forEach(({ name }, i) => {
      const key = `photo_${job_card_reference_id}_${name}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const { dataUrl, fileName } = JSON.parse(saved);
          fetch(dataUrl)
            .then((r) => r.blob())
            .then((blob) => {
              const file = new File([blob], fileName, { type: blob.type });
              const previewUrl = URL.createObjectURL(blob);
              setPhotos((prev) => {
                const next = [...prev];
                next[i] = { file, previewUrl };
                return next;
              });
            })
            .catch(() => localStorage.removeItem(key));
        } catch {
          localStorage.removeItem(key);
        }
      }
    });
  }, [job_card_reference_id]);

  const persistPhoto = (index, file) => {
    const key = `photo_${job_card_reference_id}_${photoDefs[index].name}`;
    const reader = new FileReader();
    reader.onload = (e) => {
      localStorage.setItem(key, JSON.stringify({ dataUrl: e.target.result, fileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (isSuccess) {
      photoDefs.forEach(({ name }) => {
        localStorage.removeItem(`photo_${job_card_reference_id}_${name}`);
      });
      navigate(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/job_completed/`);
    }
  }, [isSuccess]);

  const capturedCount = photos.filter(Boolean).length;
  const allCaptured = capturedCount === photoDefs.length;

  const openCamera = (index) => {
    setActiveRetakeIndex(index);
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || activeRetakeIndex === null) return;
    const previewUrl = URL.createObjectURL(file);
    setPhotos((prev) => {
      const next = [...prev];
      if (next[activeRetakeIndex]?.previewUrl) URL.revokeObjectURL(next[activeRetakeIndex].previewUrl);
      next[activeRetakeIndex] = { file, previewUrl };
      return next;
    });
    persistPhoto(activeRetakeIndex, file);
    setActiveRetakeIndex(null);
  };

  const handleSubmit = () => {
    if (!allCaptured) return;
    setIsUploading(true);
    const body = new FormData();
    body.append("reference_id", job_card_reference_id);
    body.append("state", "REF");
    // A photo kept from before a redo has no file: the job already has it.
    photoDefs.forEach(({ name }, i) => { if (photos[i].file) body.append(name, photos[i].file); });
    editJobCard({ body }).unwrap().catch((error) => {
      setIsUploading(false);
      dispatch(showError(stepSaveMessage(error)));
    });
  };

  const openMenu = (e, index) => { setMenuAnchor(e.currentTarget); setMenuIndex(index); };
  const closeMenu = () => { setMenuAnchor(null); setMenuIndex(null); };

  if (!isMobile) return <p>Sorry, this feature is only available on mobile devices.</p>;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showAlert && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleDismiss} connectionDetails={connectionDetails} position="top" alertType="error" />
      )}
      {showWeakWarning && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleWeakWarningDismiss} connectionDetails={connectionDetails} position="top" alertType="warning" responseTime={responseTime || avgResponseTime} />
      )}

      <NavbarStepper step={step} />

      <Box sx={{ px: 2, pb: "100px" }}>
        {/* Heading row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, mb: 2 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff" }}>
            {isUninstall ? "After Uninstall Photos" : "After Install Photos"}
          </Typography>
          <Box sx={{
            px: 1.2, py: 0.4, borderRadius: "20px",
            backgroundColor: allCaptured ? "rgba(34,197,94,0.15)" : "rgba(253,185,64,0.12)",
            border: `1px solid ${allCaptured ? "rgba(34,197,94,0.4)" : "rgba(253,185,64,0.3)"}`,
          }}>
            <Typography sx={{
              fontSize: "0.72rem", fontWeight: 700,
              color: allCaptured ? "#22C55E" : AMBER,
              letterSpacing: "0.05em",
            }}>
              {capturedCount} / {photoDefs.length} CAPTURED
            </Typography>
          </Box>
        </Box>

        {/* Photo tiles */}
        <Grid container spacing={1.5}>
          {photoDefs.map(({ label }, i) => {
            const photo = photos[i];
            return (
              <Grid item xs={4} key={i}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    onClick={() => !photo && openCamera(i)}
                    sx={{
                      borderRadius: "10px", overflow: "hidden",
                      border: photo ? "1.5px solid rgba(34,197,94,0.4)" : `1.5px dashed rgba(253,185,64,0.35)`,
                      backgroundColor: "#0d1117",
                      aspectRatio: "1",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: photo ? "default" : "pointer",
                    }}
                  >
                    {photo ? (
                      <img src={photo.previewUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    ) : (
                      <CameraAltOutlinedIcon sx={{ color: AMBER, opacity: 0.45, fontSize: "1.8rem" }} />
                    )}
                  </Box>
                  {photo && (
                    <IconButton
                      size="small"
                      onClick={(e) => openMenu(e, i)}
                      sx={{
                        position: "absolute", top: 2, right: 2,
                        backgroundColor: "rgba(0,0,0,0.55)", color: "#fff",
                        width: 22, height: 22,
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  )}
                </Box>
                <Typography sx={{
                  fontSize: "0.62rem", color: "rgba(255,255,255,0.55)",
                  textAlign: "center", mt: "5px", lineHeight: 1.3, whiteSpace: "pre-line",
                }}>
                  {label}
                </Typography>
                {slotNote(i, photos[i])}
              </Grid>
            );
          })}
        </Grid>

        {!allCaptured && (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", textAlign: "center", mt: 3 }}>
            Tap a tile to capture that photo
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{ sx: { backgroundColor: "#1A1C24", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }}
      >
        <MenuItem onClick={() => { openCamera(menuIndex); closeMenu(); }} sx={{ fontSize: "0.875rem" }}>
          Retake photo
        </MenuItem>
      </Menu>

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        display: "flex", gap: "12px",
        px: "16px", py: "14px",
        background: "rgba(13,14,20,0.95)", backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <AbortJobButton />
        <Button
          variant="contained"
          disabled={!allCaptured || !isConnected || isLoading || isUploading}
          onClick={handleSubmit}
          disableElevation
          sx={{
            flex: 1,
            backgroundColor: AMBER, color: "#000", fontWeight: 700,
            borderRadius: "10px", py: "13px", fontSize: "1rem",
            textTransform: "none", border: "none",
            opacity: (!allCaptured || !isConnected || isLoading || isUploading) ? 0.55 : 1,
            "&:hover": { backgroundColor: "#e6a832" },
            "&.Mui-disabled": { opacity: 0.55 },
          }}
          startIcon={(isLoading || isUploading) ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {(isLoading || isUploading) ? "Uploading..." : "Next →"}
        </Button>
      </Box>
    </>
  );
}

// ─── Dead code below — kept for reference, no longer used in the flow ──────────

/*
function BeforeInstallPage() {
  useStepperStateRestoration();

  const {
    isConnected,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails
  } = useConnectionMonitor();

  let { job_card_reference_id } = useParams();
  let { serial_number } = useParams();
  let { imsi_number } = useParams();
  const { data: job_card_data, isSuccess: isGetJobCardStateSuccess } =
    useGetJobCardByReferenceQuery(job_card_reference_id);

  const dispatch = useDispatch();

  const first_photo_name = "before_photo_on";
  const second_photo_name = "before_photo_off";
  const first_photo_heading_text =
    "Take a photo of the DB board BEFORE installation with COVER ON";
  const second_photo_heading_text =
    "Take a photo of the DB board BEFORE installation with COVER OFF";

  const [successful_navigation_link, setsuccessful_navigation_link] =
    React.useState("");
  const [update_to_state, setupdate_to_state] = React.useState("");

  useEffect(() => {
    if (isGetJobCardStateSuccess) {
      if (
        job_card_data.install_state === "UN" ||
        job_card_data.install_state === "RE"
      ) {
        dispatch(setCurrentStep(2));
        setsuccessful_navigation_link(
          `/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/${job_card_data.install_state}/`
        );
        setupdate_to_state("UNI");
      } else {
        setsuccessful_navigation_link(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/connection_test_interlude/`);
        setupdate_to_state("AMP");
        dispatch(setCurrentStep(3));
      }
    }
  }, [isGetJobCardStateSuccess]);

  return (
    <>
      {showAlert && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="error"
        />
      )}
      {showWeakWarning && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleWeakWarningDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="warning"
          responseTime={responseTime || avgResponseTime}
        />
      )}
      <PhotoCaptureProcess
        first_photo_name={first_photo_name}
        second_photo_name={second_photo_name}
        first_photo_heading_text={first_photo_heading_text}
        second_photo_heading_text={second_photo_heading_text}
        successful_navigation_link={successful_navigation_link}
        update_to_state={update_to_state}
        job_state={job_card_data?.install_state}
      />
    </>
  );
}

function PhotoCaptureProcess({
  first_photo_name,
  second_photo_name,
  first_photo_heading_text,
  second_photo_heading_text,
  successful_navigation_link,
  update_to_state,
  job_state,
}) {
  const {
    isConnected,
    showAlert,
    handleRetry,
    handleDismiss,
    connectionDetails
  } = useConnectionMonitor();

  const dispatch = useDispatch();
  let step = useSelector(getCurrentStep);
  let { job_card_reference_id } = useParams();
  let navigate = useNavigate();
  const [
    editJobCard,
    { isSuccess: isEditJobCardSuccess, isLoading: isEditJobCardLoading },
  ] = useEditJobCardMutation();

  const [photo_number, setPhotoNumber] = useState(0);
  const [first_photo, setFirstPhoto] = useState(null);
  const [second_photo, setSecondPhoto] = useState(null);

  const firstPhotoStorageKey = `photo_${job_card_reference_id}_${first_photo_name}`;
  const secondPhotoStorageKey = `photo_${job_card_reference_id}_${second_photo_name}`;

  useEffect(() => {
    const loadSavedPhoto = async (storageKey, setPhoto) => {
      const savedPhotoData = localStorage.getItem(storageKey);
      if (savedPhotoData) {
        try {
          const { dataUrl, fileName } = JSON.parse(savedPhotoData);
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          const file = new File([blob], fileName, { type: blob.type });
          setPhoto(file);
        } catch (error) {
          localStorage.removeItem(storageKey);
        }
      }
    };
    loadSavedPhoto(firstPhotoStorageKey, setFirstPhoto);
    loadSavedPhoto(secondPhotoStorageKey, setSecondPhoto);
  }, [firstPhotoStorageKey, secondPhotoStorageKey]);

  useEffect(() => {
    if (first_photo) {
      const reader = new FileReader();
      reader.onload = (e) => {
        localStorage.setItem(firstPhotoStorageKey, JSON.stringify({ dataUrl: e.target.result, fileName: first_photo.name }));
      };
      reader.readAsDataURL(first_photo);
    }
  }, [first_photo, firstPhotoStorageKey]);

  useEffect(() => {
    if (second_photo) {
      const reader = new FileReader();
      reader.onload = (e) => {
        localStorage.setItem(secondPhotoStorageKey, JSON.stringify({ dataUrl: e.target.result, fileName: second_photo.name }));
      };
      reader.readAsDataURL(second_photo);
    }
  }, [second_photo, secondPhotoStorageKey]);

  useEffect(() => {
    if (isEditJobCardSuccess) {
      localStorage.removeItem(firstPhotoStorageKey);
      localStorage.removeItem(secondPhotoStorageKey);
      navigate(successful_navigation_link);
    }
  }, [isEditJobCardSuccess, firstPhotoStorageKey, secondPhotoStorageKey, navigate, successful_navigation_link]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleSubmitPhotos = () => {
    const body = new FormData();
    body.append(first_photo_name, first_photo);
    body.append(second_photo_name, second_photo);
    body.append("reference_id", job_card_reference_id);
    body.append("state", update_to_state);
    editJobCard({ body: body });
  };

  useEffect(() => {
    if (photo_number === 1 && step === 3) {
      if (job_state != "UN") {
        dispatch(setCurrentStep(4));
        step = 4;
      } else if (photo_number === 1 && (job_state === "UN" || job_state == "RE")) {
        dispatch(setCurrentStep(3));
        step = 3;
      }
    } else if (photo_number === 1 && step === 2) {
      dispatch(setCurrentStep(3));
      step = 3;
    } else if (photo_number === 1 && step === 7) {
      dispatch(setCurrentStep(8));
      step = 8;
    } else if (photo_number === 1 && step === 9) {
      dispatch(setCurrentStep(10));
      step = 10;
    } else if (photo_number === 1 && step === 5) {
      dispatch(setCurrentStep(6));
      step = 6;
    }
  }, [photo_number]);

  if (isMobile) {
    if (photo_number === 0) {
      return (
        <>
          <NavbarStepper step={step} />
          <Grid container alignItems="center" justifyContent="center">
            <PageHeading heading_text={first_photo_heading_text} />
            <TakePhoto
              photo={first_photo}
              setPhoto={setFirstPhoto}
              setPhotoNumber={setPhotoNumber}
              photo_number={photo_number}
              isConnected={isConnected}
            />
          </Grid>
        </>
      );
    } else {
      return (
        <>
          <NavbarStepper step={step} />
          <Grid container alignItems="center" justifyContent="center">
            <PageHeading heading_text={second_photo_heading_text} />
            <TakePhoto
              photo={second_photo}
              setPhoto={setSecondPhoto}
              setPhotoNumber={setPhotoNumber}
              photo_number={photo_number}
              handleSubmitPhotos={handleSubmitPhotos}
              submit_photos_loading={isEditJobCardLoading}
              isConnected={isConnected}
            />
          </Grid>
        </>
      );
    }
  } else {
    return <p>Sorry, this feature is only available on mobile devices.</p>;
  }
}

function PageHeading({ heading_text }) {
  return (
    <>
      <Grid item xs={1} />
      <Grid item xs={10}>
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, mb: 2, lineHeight: 1.3 }}>
          {heading_text}
        </Typography>
      </Grid>
      <Grid item xs={1} />
    </>
  );
}

function TakePhoto({
  photo,
  setPhoto,
  setPhotoNumber,
  photo_number,
  handleSubmitPhotos,
  submit_photos_loading,
  isConnected,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const dispatch = useDispatch();
  const [no_photo_error, setNoPhotoError] = useState(false);
  const [are_you_sure_flag, setAreYouSureFlag] = useState(false);
  const [frontend_photo_placeholder, setFrontendPhotoPlaceholder] = useState(null);

  useEffect(() => {
    if (photo && !frontend_photo_placeholder) {
      const photoURL = URL.createObjectURL(photo);
      setFrontendPhotoPlaceholder(photoURL);
      return () => URL.revokeObjectURL(photoURL);
    }
  }, [photo]);

  useEffect(() => {
    return () => {
      if (frontend_photo_placeholder) {
        URL.revokeObjectURL(frontend_photo_placeholder);
      }
    };
  }, [frontend_photo_placeholder]);

  const handleCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsImageLoading(true);
      const photoURL = URL.createObjectURL(file);
      setPhoto(file);
      setFrontendPhotoPlaceholder(photoURL);
      setNoPhotoError(false);
      setTimeout(() => setIsImageLoading(false), 500);
    }
  };

  const HandleNextButton = () => {
    if (photo_number == 0) {
      if (photo === null) {
        setNoPhotoError(true);
      } else {
        setAreYouSureFlag(true);
        setPhotoNumber(1);
        setFrontendPhotoPlaceholder(null);
      }
    } else {
      if (photo === null) {
        setNoPhotoError(true);
      } else {
        setIsUploading(true);
        handleSubmitPhotos();
      }
    }
  };

  const navigate = useNavigate();

  return (
    <>
      <input
        accept="image/*"
        id="icon-button-file"
        type="file"
        capture="environment"
        onChange={handleCapture}
        style={{ display: "none" }}
      />

      <Grid item xs={1} />
      <Grid item xs={10}>
        <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderColor: "#FDB940", color: "inherit" }}>
          Once you've clicked next, you cannot retake this photo!
        </Alert>
      </Grid>
      <Grid item xs={1} />

      <Grid item xs={1} />
      <Grid item xs={10}>
        {photo !== null && !isImageLoading ? (
          <Box sx={{ width: "100%", aspectRatio: "1", borderRadius: "12px", overflow: "hidden" }}>
            <img src={frontend_photo_placeholder} alt="Captured" style={{ objectFit: "cover", width: "100%", height: "100%" }} />
          </Box>
        ) : (
          <Box sx={{ position: "relative", width: "100%" }}>
            <svg width="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <rect width="200" height="200" rx="12" fill="#161B22"/>
              <rect x="1" y="1" width="198" height="198" rx="11.5" fill="none" stroke="#FDB940" strokeOpacity="0.4" strokeWidth="2" strokeLinecap="square" strokeDasharray="4 3"/>
              <g transform="translate(80, 68) scale(1.6667)" fill="none" stroke="#FDB940" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </g>
              <text x="100" y="128" textAnchor="middle" fill="#ffffff" fillOpacity="0.35" fontFamily="Inter, system-ui, -apple-system, sans-serif" fontSize="11">No photo taken</text>
            </svg>
            {isImageLoading && (
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                <CircularProgress size={40} sx={{ color: "#FDB940" }} />
              </Box>
            )}
          </Box>
        )}
      </Grid>
      <Grid item xs={1} />

      <Grid item xs={1} />
      <Grid item xs={10} sx={{ mt: 2 }}>
        <label htmlFor="icon-button-file" style={{ width: "100%" }}>
          <Button
            variant="contained"
            component="span"
            fullWidth
            sx={{ ...theme.yellowButtonStyle.root, border: "none", borderRadius: "24px", py: 1.5 }}
          >
            {photo === null ? "Capture Photo" : "Re-take Picture"}
          </Button>
        </label>
      </Grid>
      <Grid item xs={1} />

      <Grid item xs={1} />
      <Grid item xs={10}>
        <Collapse in={no_photo_error}>
          <Alert severity="error" sx={{ mt: 1 }}>
            You have not taken any photos yet
          </Alert>
        </Collapse>
      </Grid>
      <Grid item xs={1} />

      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", gap: "12px", px: "16px", py: "14px", background: "rgba(13,14,20,0.95)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <AbortJobButton />
        <Button
          variant="contained"
          disableElevation
          sx={{ flex: 1, ...theme.yellowButtonStyle.root, border: "none", borderRadius: "10px", py: "13px", fontSize: "1rem", textTransform: "none", opacity: (!isConnected || submit_photos_loading || isUploading) ? 0.7 : 1, "&.Mui-disabled": { opacity: 0.7 } }}
          onClick={HandleNextButton}
          disabled={!isConnected || submit_photos_loading || isUploading}
          startIcon={(submit_photos_loading || isUploading) ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submit_photos_loading || isUploading
            ? (photo_number === 1 ? "Uploading..." : "Next →")
            : "Next →"}
        </Button>
      </Box>
    </>
  );
}

// export const MemoizedBeforeInstallPage = React.memo(BeforeInstallPage);
*/

export const MemoizedAfterInstallPage = React.memo(AfterInstallPage);
